import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";

export default function AdminLogin() {
  const { isAuthed, role, loginAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isAuthed && role === "admin") {
      navigate(location.state?.from || "/admin/dashboard", { replace: true });
    }
  }, [isAuthed, role, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginAdmin(email.trim(), password);
      toast.success("Signed in");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-md px-6 py-20">
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-[#0A1128]">
          <ShieldCheck className="h-3.5 w-3.5" /> AtlasForge Admin
        </span>
        <h1 className="font-display mt-6 text-[32px] leading-tight text-[#0A1128]">
          Internal sign in
        </h1>
        <p className="mt-3 text-[14px] text-slate-600">
          Restricted to AtlasForge Financial staff. Partner accounts should sign in at{" "}
          <a href="/partners" className="underline">/partners</a>.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-[12px] font-medium text-slate-600">Admin email</span>
            <input
              data-testid="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-slate-400"
            />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-slate-600">Password</span>
            <input
              data-testid="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-slate-400"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A1128] text-white text-[14px] font-medium px-5 py-3 hover:bg-[#1E293B] disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
